<!-- @format -->
<template>
	<div class="time-header">
		<el-select v-model="searchValue.field" clearable filterable placeholder="请选择字段" @change="onSearch">
			<el-option
				v-for="item in fieldList"
				:key="item.label"
				:label="`${item.label}(${item.value})`"
				:value="item.value"
			/>
		</el-select>
		<el-input v-model="searchValue.input" placeholder="请输入关键字, 回车进行搜索" @keyup.enter="onSearch" />
	</div>
	<el-timeline v-loading="timeList.loading" class="time-line">
		<el-timeline-item
			v-for="item in timeList.timeStampList"
			:key="item.id"
			type="primary"
			:hollow="true"
			:hide-timestamp="true"
			placement="top"
		>
			<div>
				<template v-if="item.chgInfo === 'create'">
					<el-button type="primary" link> {{ item.updatedTime }} </el-button>
					{{ item.resFrom }}
					<p> 创建 </p>
				</template>
				<template v-else>
					<el-button type="primary" link @click="onClickTimeStamp(item)"> {{ item.updatedTime || '123' }} </el-button>
					{{ item.resFrom }}
					<ElCollapseTransition>
						<el-card v-show="isActive.includes(item.id)">
							<el-table :data="item.chgInfo" style="width: 100%" max-height="500">
								<el-table-column prop="fc" label="名称" />
								<el-table-column prop="fn" label="编码" />
								<el-table-column prop="nv" label="现值" />
								<el-table-column prop="ov" label="原值" />
							</el-table>
						</el-card>
					</ElCollapseTransition>
				</template>
			</div>
		</el-timeline-item>
	</el-timeline>
</template>

<script setup lang="ts">
	import { getAllHistory } from '@/api/resource';

	interface Props {
		objectid: string;
		params?: Record<string, any>;
	}

	interface ChgInfo {
		fc: string;
		fn: string;
		nv: string;
		ov: string;
	}

	interface TimeStampList {
		id: string;
		updatedTime: string;
		resFrom: string;
		chgInfo: string | ChgInfo[];
		chgDesc: string;
	}

	interface TimeList {
		loading: boolean;
		total: number;
		data: TimeStampList[];
		timeStampList: TimeStampList[];
	}

	const props = withDefaults(defineProps<Props>(), {
		params: () => ({}),
	});

	// inject
	// 左侧树点击的节点
	const resItem: any = inject('resItem');

	const searchValue = ref<{
		input: string;
		field: string;
	}>({
		input: '',
		field: '',
	});

	const isActive = ref<string[]>([]);

	const timeList = ref<TimeList>({
		loading: false,
		total: 0,
		data: [],
		timeStampList: [],
	});

	// change过的字段列表
	const fieldList = computed(() =>
		timeList.value.data.slice(0, -1).reduce((list: { label: string; value: string }[], item) => {
			return [
				...list,
				...(item.chgInfo as ChgInfo[])
					.filter((itm) => list.every((it) => it.value !== itm.fn))
					.map((itm) => ({ label: itm.fc, value: itm.fn })),
			];
		}, []),
	);

	const onSearch = () => {
		// 如果没有查询条件则还原所有数据
		if (Object.values(searchValue.value).every((i) => !i)) {
			timeList.value.timeStampList = timeList.value.data;
			isActive.value = [];
			return false;
		}

		const data = toRaw(timeList.value.data)
			.slice(0, -1)
			.reduce((list: TimeStampList[], item) => {
				const filterArr = (item.chgInfo as ChgInfo[]).filter(
					(itm) =>
						(!searchValue.value.field || searchValue.value.field === itm.fn) &&
						(!searchValue.value.input ||
							Object.values(itm)
								.map((it) => it.toLocaleLowerCase())
								.includes(searchValue.value.input.toLocaleLowerCase())),
				);

				if (filterArr.length) {
					return [...list, { ...item, chgInfo: filterArr }];
				} else {
					return list;
				}
			}, []);

		timeList.value.timeStampList = data;
		isActive.value = data.map((i) => i.id);
	};

	const onClickTimeStamp = (item) => {
		const idx = isActive.value.findIndex((itm) => itm === item.id);
		if (idx === -1) {
			isActive.value = [...isActive.value, item.id];
		} else {
			isActive.value.splice(idx, 1);
		}
	};

	const getTimeList = async () => {
		timeList.value.loading = true;
		if (!props.objectid) {
			return false;
		}
		const res = await getAllHistory({
			objectid: props.objectid,
			resCode: resItem.value.resCode,
			...props.params,
		});
		timeList.value.loading = false;
		if (res.code === 200) {
			timeList.value.total = res.total;
			const data = res.rows.map((itm) => ({
				...itm,
				chgInfo: itm.chgInfo === 'create' ? itm.chgInfo : JSON.parse(itm.chgInfo || '{}'),
			}));
			timeList.value.data = data;
			timeList.value.timeStampList = data;
		}
	};

	onMounted(() => {
		getTimeList();
	});
</script>

<style scoped>
	.time-header {
		margin: 0 10px 20px 10px;
		display: flex;
		gap: 5px;
	}

	:deep(.el-card__body) {
		padding: 10px;
	}

	.time-line {
		height: calc(100% - 52px);
		overflow-y: scroll;
		padding: 0 10px;
	}
</style>
